import { useQuery } from '@tanstack/react-query';
import { listRetailerRegionsV1RetailerRegionsGetOptions, useGetRetailersForOrganizationQuery } from '@vizit/api';
import { Button, ButtonLevel, ButtonSize, Skeleton, Text, TextColor, TextWeight } from '@vizit/atoms';
import { FEATURE_FLAGS, RetailerKey } from '@vizit/constants';
import { useFeatureFlag } from '@vizit/feature-flags';
import { Copy01Icon, LogIn01Icon } from '@vizit/icons';
import { Card, type CardProps } from '@vizit/molecules';
import { cx } from '@vizit/theme';
import * as React from 'react';
import { OrganizationDialog, type OrganizationFormSchema } from '../organization-dialog';

interface OrganizationDetailCardProps extends CardProps {
  /**
   * The account type of the organization
   */
  accountType: string;
  /**
   * The organization's default retailer
   */
  defaultRetailer?: string | null;
  /**
   * The date the organization was created
   */
  createdAt?: Date;
  /**
   * The user who created the organization
   */
  createdBy: string;
  /**
   * Whether the organization is disabled
   */
  isDisabled?: boolean;
  /**
   * Whether the organization is loading
   */
  isLoading?: boolean;
  /**
   * The ICP (L1) product categories of the organization
   */
  icpProductCategories: Record<string, string>;
  /**
   * The name of the organization
   */
  name: string;
  /**
   * The organization ID
   */
  organizationId: string;
  /**
   * The portfolio ID
   */
  portfolioId: string;
  /**
   * The ICP (L1) product categories of the organization
   */
  productCategories: string[];
  /**
   * The function to edit the organization
   */
  onEditOrganization: (
    data: OrganizationFormSchema,
    defaultValues: OrganizationFormSchema,
    organizationId: string,
  ) => Promise<void> | void;
  /**
   * The function to view the organization
   */
  onViewOrganization: (organizationId: string, organizationName: string, portfolioId: string) => Promise<void> | void;
}

interface OrganizationDetailsGridCellProps {
  /**
   * The label of the cell
   */
  label: React.ReactNode;
  /**
   * The value of the cell
   */
  value: React.ReactNode;
}

function OrganizationDetailsGridCell(props: OrganizationDetailsGridCellProps) {
  const { label, value } = props;

  const handleCopyToClipboard = React.useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  }, []);

  const isInternalOrgId = React.useMemo(
    () => typeof label === 'string' && label.includes('Internal Organization ID'),
    [label],
  );

  return (
    <>
      <Text color={TextColor.Quaternary} weight={TextWeight.Semibold} showEllipsis={false} className="col-span-1">
        {label}
      </Text>
      <div className="col-span-3 max-sm:col-span-1 flex items-center gap-2">
        {isInternalOrgId ? (
          <div className="flex items-center gap-8">
            <div>
              <code className="bg-gray-utility-200 text-sm font-mono flex-1 py-2 min-w-0">{value}</code>
            </div>
            <Button
              level={ButtonLevel.Secondary}
              size={ButtonSize.Sm}
              onPress={() => handleCopyToClipboard(String(value))}
              iconStart={<Copy01Icon color="currentColor" size={12} />}
              className="flex-shrink-0 text-xs"
            >
              Copy
            </Button>
          </div>
        ) : (
          <Text showEllipsis={false} className="flex-1">
            {value}
          </Text>
        )}
      </div>
    </>
  );
}

interface OrganizationDetailsGridCellLoadingProps {
  /**
   * The label of the cell
   */
  label: React.ReactNode;
}

function OrganizationDetailsGridCellLoading(props: OrganizationDetailsGridCellLoadingProps) {
  const { label } = props;

  const isProductCategories = React.useMemo(() => label === 'ICP Product Categories:', [label]);
  return (
    <>
      <Text showEllipsis={false} className="font-semibold text-secondary col-span-1">
        {label}
      </Text>
      <div className="col-span-3 h-12 flex items-center max-sm:col-span-1">
        {isProductCategories ? (
          <div className="flex flex-wrap gap-3">
            <Skeleton className="h-7 w-20" variant="rounded" />
            <Skeleton className="h-7 w-24" variant="rounded" />
            <Skeleton className="h-7 w-18" variant="rounded" />
            <Skeleton className="h-7 w-28" variant="rounded" />
          </div>
        ) : (
          <Skeleton className="h-7 w-34" variant="square" />
        )}
      </div>
    </>
  );
}

function OrganizationDetailCard(props: OrganizationDetailCardProps) {
  const {
    accountType,
    className,
    createdAt,
    createdBy,
    defaultRetailer,
    isDisabled = false,
    isLoading = false,
    icpProductCategories,
    name,
    organizationId,
    portfolioId,
    productCategories,
    onEditOrganization,
    onViewOrganization,
    ...other
  } = props;

  const dateText = React.useMemo(
    () => createdAt?.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) || '',
    [createdAt],
  );

  const isRetailerEnabled = useFeatureFlag(FEATURE_FLAGS.RETAILER, false);

  const { data: allRetailersData } = useQuery({
    ...listRetailerRegionsV1RetailerRegionsGetOptions(),
    enabled: isRetailerEnabled,
  });

  const retailerRegions = React.useMemo(() => {
    if (!isRetailerEnabled || !allRetailersData) return undefined;
    const allowed: string[] = [RetailerKey.AmazonUS, RetailerKey.WalmartUS];
    return allRetailersData
      .filter((r) => allowed.includes(r.id))
      .reduce(
        (acc, r) => {
          acc[r.id] = r.display_name;
          return acc;
        },
        {} as Record<string, string>,
      );
  }, [isRetailerEnabled, allRetailersData]);

  const { data: orgRetailersData } = useGetRetailersForOrganizationQuery(
    { org_id: organizationId },
    { enabled: isRetailerEnabled && !!organizationId },
  );

  const defaultValues = React.useMemo(
    () => ({
      organizationName: name,
      accountType: accountType,
      categories: productCategories,
      retailers: isRetailerEnabled ? (orgRetailersData?.map((r) => r.id) ?? [RetailerKey.AmazonUS]) : [],
    }),
    [name, accountType, productCategories, isRetailerEnabled, orgRetailersData],
  );

  const productCategoriesText = React.useMemo(() => {
    return productCategories.map((category) => icpProductCategories[category]).join(', ');
  }, [productCategories, icpProductCategories]);

  const defaultRetailerText = defaultRetailer || 'None';

  const handleOnViewOrganization = React.useCallback(() => {
    onViewOrganization(organizationId, name, portfolioId);
  }, [onViewOrganization, organizationId, name, portfolioId]);

  const handleOnEditOrganization = React.useCallback(
    (data: OrganizationFormSchema) => {
      onEditOrganization(data, defaultValues, organizationId);
    },
    [onEditOrganization, defaultValues, organizationId],
  );

  return (
    <Card className={cx('min-w-600 w-full max-w-4xl', className)} {...other}>
      <Card.Header>
        <div className="grid grid-cols-4 gap-5 min-w-600 py-4 max-sm:grid-cols-2">
          {isLoading ? (
            <>
              <OrganizationDetailsGridCellLoading label="Account Type:" />
              <OrganizationDetailsGridCellLoading label="Default Retailer:" />
              <OrganizationDetailsGridCellLoading label="ICP Product Categories:" />
              <OrganizationDetailsGridCellLoading label="Created At:" />
              <OrganizationDetailsGridCellLoading label="Created By:" />
              <OrganizationDetailsGridCellLoading label="Internal Organization ID:" />
            </>
          ) : (
            <>
              <OrganizationDetailsGridCell label="Account Type:" value={accountType} />
              <OrganizationDetailsGridCell label="Default Retailer:" value={defaultRetailerText} />
              <OrganizationDetailsGridCell label="ICP Product Categories:" value={productCategoriesText} />
              <OrganizationDetailsGridCell label="Created At:" value={dateText} />
              <OrganizationDetailsGridCell label="Created By:" value={createdBy} />
              <OrganizationDetailsGridCell label="Internal Organization ID:" value={organizationId} />
            </>
          )}
        </div>
      </Card.Header>
      <Card.Footer className="flex flex-row gap-4">
        <OrganizationDialog
          variant="edit"
          id={organizationId ?? ''}
          isDisabled={isLoading || isDisabled}
          icpProductCategories={icpProductCategories}
          retailerRegions={isRetailerEnabled ? retailerRegions : undefined}
          disabledRetailers={isRetailerEnabled ? [RetailerKey.AmazonUS] : undefined}
          onSubmit={handleOnEditOrganization}
          withIcon={true}
          defaultValues={defaultValues}
        />
        <Button
          isDisabled={isLoading || isDisabled}
          level={ButtonLevel.Primary}
          size={ButtonSize.Md}
          className="text-sm font-semibold"
          onPress={handleOnViewOrganization}
          iconStart={<LogIn01Icon className="text-brand-400" size={20} />}
        >
          Login
        </Button>
      </Card.Footer>
    </Card>
  );
}

export { OrganizationDetailCard };
